import type { CustomerReservationMutationResult } from "@billiard/contracts";
import {
  isCreateCustomerReservationRequest,
  isCustomerReservationMutationResult,
  isCustomerReservationReadModel,
  isCustomerReservationTransitionRequest,
  isTableSnapshot,
} from "@billiard/contracts";
import type { CustomerReservationMutationCommand } from "./api";

export const CUSTOMER_RESERVATION_RECOVERY_STORAGE_KEY =
  "billiard.ops-web.customer-reservation-recovery.v1";

export interface CustomerReservationRecoveryContext {
  tenantId: string;
  storeId: string;
  actorId: string;
}

export type CustomerReservationRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN" | "RETRY_ALLOWED";

export interface CustomerReservationRecovery extends CustomerReservationRecoveryContext {
  command: CustomerReservationMutationCommand;
  state: CustomerReservationRecoveryState;
}

export type CustomerReservationRecoveryView =
  | { status: "NONE"; canRetry: false }
  | { status: "OWNED"; recovery: CustomerReservationRecovery; canRetry: boolean }
  | { status: "CONTEXT_MISMATCH" | "COMMAND_MISMATCH"; canRetry: false }
  | {
      status: "RECOVERY_STORAGE_UNAVAILABLE";
      canRetry: false;
      pendingOutcomeMayExist: boolean;
    };

export type CustomerReservationAuthorityResolution =
  | { status: "EXECUTED"; result: CustomerReservationMutationResult }
  | { status: "RETRY_ALLOWED"; recovery: CustomerReservationRecovery }
  | { status: "FAIL_CLOSED" };

let pendingRecovery: CustomerReservationRecovery | null = null;
let dispatchingRecovery: CustomerReservationRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function contextIsValid(value: unknown): value is CustomerReservationRecoveryContext {
  return (
    isRecord(value) &&
    typeof value.tenantId === "string" &&
    value.tenantId.length > 0 &&
    typeof value.storeId === "string" &&
    value.storeId.length > 0 &&
    typeof value.actorId === "string" &&
    value.actorId.length > 0
  );
}

function commandIsValid(
  command: unknown,
  context: CustomerReservationRecoveryContext,
): command is CustomerReservationMutationCommand {
  if (!isRecord(command) || !isRecord(command.context)) return false;
  if (
    command.context.tenantId !== context.tenantId ||
    command.context.storeId !== context.storeId ||
    command.context.actorId !== context.actorId
  ) {
    return false;
  }
  if (command.kind === "CREATE") return isCreateCustomerReservationRequest(command.request);
  if (
    typeof command.reservationId !== "string" ||
    !isCustomerReservationReadModel(command.baseline) ||
    command.baseline.reservationId !== command.reservationId
  ) {
    return false;
  }
  if (command.kind === "TRANSITION") {
    return (
      isCustomerReservationTransitionRequest(command.request) &&
      command.request.expectedReservationVersion === command.baseline.reservationVersion &&
      (command.request.action !== "RETRACT_CALL" || command.baseline.state === "CALLED")
    );
  }
  return (
    command.kind === "SEAT" &&
    command.baseline.state === "CALLED" &&
    isRecord(command.request) &&
    typeof command.request.expectedReservationVersion === "number" &&
    typeof command.request.tableId === "string" &&
    typeof command.request.expectedTableRevision === "number" &&
    typeof command.request.idempotencyKey === "string" &&
    isTableSnapshot(command.table) &&
    command.request.tableId === command.table.tableId &&
    command.request.expectedTableRevision === command.table.revision
  );
}

function immutableRecovery(
  context: CustomerReservationRecoveryContext,
  command: CustomerReservationMutationCommand,
  state: CustomerReservationRecoveryState,
): CustomerReservationRecovery {
  const snapshot = structuredClone({ ...context, command, state }) as CustomerReservationRecovery;
  const freezeDeep = (value: unknown): void => {
    if (!value || typeof value !== "object" || Object.isFrozen(value)) return;
    for (const child of Object.values(value)) freezeDeep(child);
    Object.freeze(value);
  };
  freezeDeep(snapshot);
  return snapshot;
}

function storedRecovery(value: unknown): CustomerReservationRecovery | null {
  if (!isRecord(value) || value.version !== 1 || !isRecord(value.recovery)) return null;
  const context = {
    tenantId: value.recovery.tenantId,
    storeId: value.recovery.storeId,
    actorId: value.recovery.actorId,
  };
  if (
    !contextIsValid(context) ||
    !commandIsValid(value.recovery.command, context) ||
    !["CLAIMED", "OUTCOME_UNKNOWN", "RETRY_ALLOWED"].includes(String(value.recovery.state))
  ) {
    return null;
  }
  return immutableRecovery(
    context,
    value.recovery.command,
    value.recovery.state as CustomerReservationRecoveryState,
  );
}

function storageAccess(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function recoveryEquals(
  left: CustomerReservationRecovery,
  right: CustomerReservationRecovery,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function readStored(storage: Storage): CustomerReservationRecovery | null {
  const encoded = storage.getItem(CUSTOMER_RESERVATION_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStored(recovery: CustomerReservationRecovery): boolean {
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    storage.setItem(
      CUSTOMER_RESERVATION_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const saved = readStored(storage);
    return saved !== null && recoveryEquals(saved, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const storage = storageAccess();
  if (!storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = storage.getItem(CUSTOMER_RESERVATION_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStored(pendingRecovery)) recoveryUnavailable = true;
  } catch {
    recoveryUnavailable = true;
  }
}

function contextMatches(
  recovery: CustomerReservationRecovery,
  context: CustomerReservationRecoveryContext,
): boolean {
  return (
    recovery.tenantId === context.tenantId &&
    recovery.storeId === context.storeId &&
    recovery.actorId === context.actorId
  );
}

function unavailableView(): CustomerReservationRecoveryView {
  return {
    status: "RECOVERY_STORAGE_UNAVAILABLE",
    canRetry: false,
    pendingOutcomeMayExist,
  };
}

export function customerReservationRecoveryContext(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): CustomerReservationRecoveryContext {
  return { tenantId: operator.tenantId, storeId: operator.storeId, actorId: operator.id };
}

export function readCustomerReservationRecovery(
  context: CustomerReservationRecoveryContext,
): CustomerReservationRecoveryView {
  hydrate();
  if (recoveryUnavailable || !contextIsValid(context)) return unavailableView();
  if (!pendingRecovery) return { status: "NONE", canRetry: false };
  if (!contextMatches(pendingRecovery, context)) {
    return { status: "CONTEXT_MISMATCH", canRetry: false };
  }
  return {
    status: "OWNED",
    recovery: pendingRecovery,
    canRetry: pendingRecovery.state === "RETRY_ALLOWED",
  };
}

export function customerReservationRecoveryBlocksReservation(reservationId: string): boolean {
  hydrate();
  if (recoveryUnavailable) return pendingOutcomeMayExist;
  if (!pendingRecovery) return false;
  return pendingRecovery.command.kind === "CREATE"
    ? true
    : pendingRecovery.command.reservationId === reservationId;
}

export function customerReservationRecoveryBlocksTable(tableId: string): boolean {
  hydrate();
  if (recoveryUnavailable) return pendingOutcomeMayExist;
  return (
    pendingRecovery?.command.kind === "SEAT" && pendingRecovery.command.table.tableId === tableId
  );
}

export function claimCustomerReservationRecovery(
  context: CustomerReservationRecoveryContext,
  command: CustomerReservationMutationCommand,
): CustomerReservationRecoveryView {
  const current = readCustomerReservationRecovery(context);
  if (current.status === "OWNED") {
    return JSON.stringify(current.recovery.command) === JSON.stringify(command)
      ? current
      : { status: "COMMAND_MISMATCH", canRetry: false };
  }
  if (current.status !== "NONE") return current;
  if (!commandIsValid(command, context)) {
    recoveryUnavailable = true;
    return unavailableView();
  }
  const recovery = immutableRecovery(context, command, "CLAIMED");
  if (!saveStored(recovery)) {
    recoveryUnavailable = true;
    return unavailableView();
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery, canRetry: false };
}

function verify(
  recovery: CustomerReservationRecovery,
  context: CustomerReservationRecoveryContext,
): boolean {
  hydrate();
  if (recoveryUnavailable || pendingRecovery !== recovery || !contextMatches(recovery, context)) {
    return false;
  }
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    const stored = readStored(storage);
    if (!stored || !recoveryEquals(stored, recovery)) {
      recoveryUnavailable = true;
      return false;
    }
    return true;
  } catch {
    recoveryUnavailable = true;
    return false;
  }
}

export function beginCustomerReservationDispatch(
  recovery: CustomerReservationRecovery,
  context: CustomerReservationRecoveryContext,
): boolean {
  if (dispatchingRecovery || recovery.state === "OUTCOME_UNKNOWN" || !verify(recovery, context)) {
    return false;
  }
  dispatchingRecovery = recovery;
  return true;
}

export function finishCustomerReservationDispatch(
  recovery: CustomerReservationRecovery,
  context: CustomerReservationRecoveryContext,
): boolean {
  if (dispatchingRecovery !== recovery || !contextMatches(recovery, context)) return false;
  dispatchingRecovery = null;
  return true;
}

export function markCustomerReservationOutcomeUnknown(
  recovery: CustomerReservationRecovery,
  context: CustomerReservationRecoveryContext,
): boolean {
  if (!verify(recovery, context)) return false;
  const unknown = immutableRecovery(context, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStored(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

export function clearCustomerReservationRecovery(
  recovery: CustomerReservationRecovery,
  context: CustomerReservationRecoveryContext,
): boolean {
  if (!verify(recovery, context)) return false;
  if (typeof window !== "undefined") {
    const storage = storageAccess();
    if (!storage) return false;
    try {
      storage.removeItem(CUSTOMER_RESERVATION_RECOVERY_STORAGE_KEY);
      if (storage.getItem(CUSTOMER_RESERVATION_RECOVERY_STORAGE_KEY) !== null) return false;
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  if (dispatchingRecovery === recovery) dispatchingRecovery = null;
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

function resultMatchesCommand(
  result: CustomerReservationMutationResult,
  recovery: CustomerReservationRecovery,
): boolean {
  const command = recovery.command;
  if (
    !isCustomerReservationMutationResult(result) ||
    result.reservation.tenantId !== recovery.tenantId ||
    result.reservation.storeId !== recovery.storeId ||
    result.reservation.lastActionBy !== recovery.actorId
  ) {
    return false;
  }
  if (command.kind === "CREATE") {
    return (
      result.operation === "CREATE" &&
      result.reservation.customerMobile === command.request.customerMobile
    );
  }
  const matchesMutation =
    result.reservation.reservationId === command.reservationId &&
    result.reservation.reservationVersion === command.baseline.reservationVersion + 1 &&
    result.operation === (command.kind === "SEAT" ? "SEAT" : command.request.action);
  if (!matchesMutation || command.kind !== "TRANSITION") return matchesMutation;
  if (command.request.action !== "RETRACT_CALL") return true;
  const returned = result.reservation;
  const baseline = command.baseline;
  return (
    baseline.state === "CALLED" &&
    returned.state === "WAITING" &&
    returned.reservationNumber === baseline.reservationNumber &&
    returned.businessDate === baseline.businessDate &&
    returned.customerName === baseline.customerName &&
    returned.customerMobile === baseline.customerMobile &&
    returned.partySize === baseline.partySize &&
    returned.requestedStartAt === baseline.requestedStartAt &&
    returned.requestedEndAt === baseline.requestedEndAt &&
    returned.note === baseline.note &&
    returned.depositFen === baseline.depositFen &&
    returned.discountFen === baseline.discountFen &&
    returned.source === baseline.source &&
    returned.confirmedAt === baseline.confirmedAt &&
    returned.arrivedAt === baseline.arrivedAt &&
    returned.queueEnteredAt === baseline.queueEnteredAt &&
    returned.calledAt === null &&
    returned.seatedAt === baseline.seatedAt &&
    returned.cancelledAt === baseline.cancelledAt &&
    returned.noShowAt === baseline.noShowAt &&
    returned.expiredAt === baseline.expiredAt &&
    returned.assignedTableId === baseline.assignedTableId &&
    returned.assignedTableNumber === baseline.assignedTableNumber &&
    returned.sessionId === baseline.sessionId &&
    returned.orderId === baseline.orderId &&
    returned.orderNumber === baseline.orderNumber &&
    returned.createdBy === baseline.createdBy &&
    returned.createdAt === baseline.createdAt
  );
}

export function reconcileCustomerReservationAuthority(
  recovery: CustomerReservationRecovery,
  context: CustomerReservationRecoveryContext,
  result: CustomerReservationMutationResult | null,
): CustomerReservationAuthorityResolution {
  if (recovery.state !== "OUTCOME_UNKNOWN" || !verify(recovery, context)) {
    return { status: "FAIL_CLOSED" };
  }
  if (result !== null) {
    if (!resultMatchesCommand(result, recovery)) return { status: "FAIL_CLOSED" };
    return clearCustomerReservationRecovery(recovery, context)
      ? { status: "EXECUTED", result }
      : { status: "FAIL_CLOSED" };
  }
  const retryable = immutableRecovery(context, recovery.command, "RETRY_ALLOWED");
  if (!saveStored(retryable)) {
    recoveryUnavailable = true;
    return { status: "FAIL_CLOSED" };
  }
  pendingRecovery = retryable;
  return { status: "RETRY_ALLOWED", recovery: retryable };
}

export function resetCustomerReservationRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  try {
    storageAccess()?.removeItem(CUSTOMER_RESERVATION_RECOVERY_STORAGE_KEY);
  } catch {
    // The next test starts with a fresh in-memory fail-closed state.
  }
}

export function resetCustomerReservationRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
